import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/blog/ProtectedRoute";
import { AdminLayout } from "@/components/blog/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw, TrendingUp, Search, Link2, Globe, AlertTriangle, UploadCloud } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const DEFAULT_DOMAIN = "ricksblackcarservicesantonio.com";
const DB = "us";

type Row = Record<string, string>;

function rowsFromResponse(data: any): Row[] {
  // gateway returns { data: { columnNames, rows }, status }
  const cols: string[] = data?.data?.columnNames ?? [];
  const rows: any[][] = data?.data?.rows ?? [];
  return rows.map((r) => {
    const o: Row = {};
    cols.forEach((c, i) => (o[c] = String(r[i] ?? "")));
    return o;
  });
}

async function callSemrush(method: string, params: Record<string, any>) {
  const { data, error } = await supabase.functions.invoke("semrush-proxy", {
    body: { method, params },
  });
  if (error) throw error;
  if (data?.error === "quota_exceeded") {
    throw new Error(data.message);
  }
  if (data?.error) throw new Error(typeof data.error === "string" ? data.error : "Semrush error");
  return data;
}

export default function AdminSEO() {
  const { toast } = useToast();
  const [domain, setDomain] = useState(DEFAULT_DOMAIN);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [overview, setOverview] = useState<Row | null>(null);
  const [keywords, setKeywords] = useState<Row[]>([]);
  const [competitors, setCompetitors] = useState<Row[]>([]);
  const [backlinks, setBacklinks] = useState<Row | null>(null);
  const [refDomains, setRefDomains] = useState<Row[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [sitemapResult, setSitemapResult] = useState<any>(null);

  const resubmitSitemap = async () => {
    setSubmitting(true);
    setSitemapResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("submit-sitemap", { body: {} });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      setSitemapResult(data);
      toast({
        title: "Sitemap resubmitted",
        description: `${data.url_count} URLs sent to ${data.property}`,
      });
    } catch (e: any) {
      toast({
        title: "Sitemap resubmission failed",
        description: e?.message ?? "Unknown error",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [ovr, kws, comps, bl, rd] = await Promise.all([
        callSemrush("domain_ranks", {
          domain, database: DB,
          export_columns: "Db,Dn,Rk,Or,Ot,Oc,Ad,At,Ac",
        }),
        callSemrush("domain_organic", {
          domain, database: DB, display_limit: 25,
          export_columns: "Ph,Po,Pp,Nq,Cp,Co,Tr,Tc,Ur",
        }),
        callSemrush("domain_organic_organic", {
          domain, database: DB, display_limit: 15,
          export_columns: "Dn,Cr,Np,Or,Ot,Oc,Ad",
        }),
        callSemrush("backlinks_overview", {
          target: domain, target_type: "root_domain",
          export_columns: "ascore,total,domains_num,urls_num,ips_num,follows_num,nofollows_num,texts_num,images_num",
        }),
        callSemrush("backlinks_refdomains", {
          target: domain, target_type: "root_domain", display_limit: 15,
          export_columns: "domain,ascore,backlinks_num,first_seen,last_seen",
        }),
      ]);

      setOverview(rowsFromResponse(ovr)[0] ?? null);
      setKeywords(rowsFromResponse(kws));
      setCompetitors(rowsFromResponse(comps));
      setBacklinks(rowsFromResponse(bl)[0] ?? null);
      setRefDomains(rowsFromResponse(rd));
    } catch (e: any) {
      const msg = e?.message ?? "Failed to load Semrush data";
      setErr(msg);
      toast({ title: "SEO data error", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stat = (label: string, value: string | number | undefined, Icon: any) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">{label}</CardTitle>
        <Icon className="h-4 w-4 text-primary" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value ?? "—"}</div>
      </CardContent>
    </Card>
  );

  return (
    <ProtectedRoute>
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold">SEO Dashboard</h1>
              <p className="text-muted-foreground">Live Semrush data for your domain</p>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label htmlFor="domain" className="text-xs">Domain</Label>
                <Input id="domain" value={domain} onChange={(e) => setDomain(e.target.value)} className="w-72" />
              </div>
              <Button onClick={load} disabled={loading} className="gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Refresh
              </Button>
            </div>
          </div>

          {err && (
            <Card className="border-destructive/40 bg-destructive/5">
              <CardContent className="pt-6 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                <div className="text-sm">{err}</div>
              </CardContent>
            </Card>
          )}

          {/* Overview stats */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">Sitemap &amp; Google Search Console</CardTitle>
              <Button onClick={resubmitSitemap} disabled={submitting} className="gap-2" variant="secondary">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                Resubmit sitemap
              </Button>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-1">
              <p>
                The sitemap is generated automatically from live routes and published posts at{" "}
                <a
                  href="https://ricksblackcarservicesantonio.com/sitemap.xml"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  /sitemap.xml
                </a>
                . It is resubmitted to Search Console automatically whenever a post is published, and after each release.
              </p>
              {sitemapResult && (
                <p className="text-foreground">
                  Last run: {sitemapResult.url_count} URLs → {sitemapResult.property}
                  {sitemapResult.search_console?.lastSubmitted
                    ? ` (Search Console last submitted ${new Date(sitemapResult.search_console.lastSubmitted).toLocaleString()})`
                    : ""}
                </p>
              )}
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {stat("Organic Keywords", overview?.Or, Search)}
            {stat("Est. Monthly Traffic", overview?.Ot, TrendingUp)}
            {stat("Authority Score", backlinks?.ascore, Globe)}
            {stat("Referring Domains", backlinks?.domains_num, Link2)}
          </div>

          {/* Top organic keywords */}
          <Card>
            <CardHeader>
              <CardTitle>Top Organic Keywords</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-muted-foreground border-b">
                    <tr>
                      <th className="py-2 pr-4">Keyword</th>
                      <th className="py-2 pr-4">Pos</th>
                      <th className="py-2 pr-4">Prev</th>
                      <th className="py-2 pr-4">Volume</th>
                      <th className="py-2 pr-4">CPC</th>
                      <th className="py-2 pr-4">Traffic %</th>
                      <th className="py-2 pr-4">URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keywords.map((k, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-medium">{k.Ph}</td>
                        <td className="py-2 pr-4">{k.Po}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{k.Pp}</td>
                        <td className="py-2 pr-4">{k.Nq}</td>
                        <td className="py-2 pr-4">${k.Cp}</td>
                        <td className="py-2 pr-4">{k.Tr}</td>
                        <td className="py-2 pr-4 max-w-xs truncate">
                          <a href={k.Ur} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                            {k.Ur?.replace(/^https?:\/\//, "")}
                          </a>
                        </td>
                      </tr>
                    ))}
                    {!loading && keywords.length === 0 && (
                      <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No data</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Competitors */}
            <Card>
              <CardHeader><CardTitle>Organic Competitors</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="text-left text-muted-foreground border-b">
                    <tr>
                      <th className="py-2 pr-4">Domain</th>
                      <th className="py-2 pr-4">Common Kw</th>
                      <th className="py-2 pr-4">Keywords</th>
                      <th className="py-2 pr-4">Traffic</th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitors.map((c, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-medium">{c.Dn}</td>
                        <td className="py-2 pr-4">{c.Cr}</td>
                        <td className="py-2 pr-4">{c.Or}</td>
                        <td className="py-2 pr-4">{c.Ot}</td>
                      </tr>
                    ))}
                    {!loading && competitors.length === 0 && (
                      <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">No data</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Top referring domains */}
            <Card>
              <CardHeader><CardTitle>Top Referring Domains</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="text-left text-muted-foreground border-b">
                    <tr>
                      <th className="py-2 pr-4">Domain</th>
                      <th className="py-2 pr-4">AS</th>
                      <th className="py-2 pr-4">Backlinks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refDomains.map((d, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-medium">{d.domain}</td>
                        <td className="py-2 pr-4">{d.ascore}</td>
                        <td className="py-2 pr-4">{d.backlinks_num}</td>
                      </tr>
                    ))}
                    {!loading && refDomains.length === 0 && (
                      <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">No data</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>

          <p className="text-xs text-muted-foreground">Data powered by Semrush.</p>
        </div>
      </AdminLayout>
    </ProtectedRoute>
  );
}